// Ablauf eines Runs (R27): Ereignisse (E24, E25, E27) und Aufträge (E4) am Ufer.
// Aufgeteilt aus run.ts.
import {
  artifacts as allArtifacts,
  reportTexts,
  getEncounter,
  getEnemy,
  baseItemId,
  getItem,
  getSpecies,
  itemLabel,
  itemText,
  items as allItems,
  letterParts,
  runRules,
  type EventDef,
  type EventEffect,
  type EventOption,
  type Rarity,
  type Wording,
  artifactOffered,
  itemOffered,
  attemptTime,
} from '../../content/index.ts';
import { type Rng } from '../rng.ts';
import type { EnemyPlacement } from '../types.ts';
import { generateFoes } from './foes.ts';
import { displayName, maxHp, xpForLevel } from './profile.ts';
import { newItem, rollRange, rollShare } from '../luck.ts';
import { pickByRarity } from './rarity.ts';
import { remember } from './bonds.ts';
import { rollSign } from './river.ts';
import type {
  ErrandReport,
  Pending,
  RunFriend,
  RunState,
} from './types.ts';
import {
  withRng,
  pick,
  available,
  friendById,
  chronicle,
  heal,
  weakestHealItem,
  makeFriend,
  gainXp,
  leave,
  bossRequest,
  stormClockOn,
  startFight,
  spend,
  optionTime,
  uniqueName,
  starFactor,
} from './helpers.ts';

export function eventVariant(run: RunState, def: EventDef): number | undefined {
  const i = def.variants?.findIndex((v) => run.flags[v.ifFlag]);
  return i !== undefined && i >= 0 ? i : undefined;
}

export function startEvent(run: RunState, party: string[], eventId: string): void {
  const def = getEncounter(eventId);
  run.usedEvents.push(eventId);
  const pending: Pending = { kind: 'event', eventId, party, log: [], done: false };
  const v = eventVariant(run, def);
  if (v !== undefined) pending.variant = v;
  // Fabi (Runde 7): Einleitungen gibt es in mehreren Fassungen
  const intro = v !== undefined ? def.variants![v]!.text : def.text;
  if (Array.isArray(intro) && intro.length > 1) pending.textIndex = withRng(run, (rng) => Math.floor(rng.next() * intro.length));
  if (def.subject === 'animal') {
    pending.subject = withRng(run, (rng) => {
      const [lo, hi] = runRules.enemyBudget.eventLevel[run.day - 1] ?? [1, 1];
      return makeFriend(run, rng, { level: lo! + Math.floor(rng.next() * (hi! - lo! + 1)), how: 'ereignis', ...(def.subjectSpecies ? { species: def.subjectSpecies } : {}) });
    });
    if (def.letter) pending.letter = withRng(run, (rng) => writeLetter(rng, pending.subject!));
  }
  // E35 Amselstreit (Runde 7): Worum gestritten wird, steht schon in der Einleitung
  if (def.prize) pending.prize = withRng(run, (rng) => rollItem(rng, def.prize!, undefined, starFactor(run)));
  run.pending = pending;
  // T36 (Fabi, Phase Kern, 17.10.2026 nachts): Gibt es nichts zu wählen, etwa beim Dank der Haselmaus, passiert es gleich;
  // Einleitung und Ergebnis stehen in einem Fenster, statt dass man erst „Danke“ und dann „Zurück zum Boot“ drückt
  const auto = v !== undefined ? def.variants![v]!.auto : def.auto;
  if (auto) {
    const only = eventOptions(run).filter((o) => o.available && !o.hidden);
    if (only.length === 1 && !only[0]!.actors && !only[0]!.gifts) {
      chooseOption(run, only[0]!.id);
      pending.auto = true;
    }
  }
}

/**
 * E28 (Fabi): ein kleines Bewerbungsschreiben, worin das Tier gut ist und was es an Bord gern tun will.
 * Spieltest Phase 4 (Fabi): Der Hauptteil ist ein Absatz, damit man die Bausteine nicht so merkt. Der Grund hängt nur
 * am Wunsch mitzureisen („Ich würde so gern mit euch reisen, denn …“), nie an einem anderen Baustein, sonst entsteht
 * Unsinn wie „Ich teile meine Nüsse, denn ich finde keine mehr“. Reihenfolge: wer ich bin, was ich kann, warum ich mitwill,
 * was ich an Bord tun würde, Schluss.
 */
export function writeLetter(rng: Rng, f: RunFriend): string {
  const L = letterParts;
  const sp = L.bySpecies[f.species];
  const spDef = getSpecies(f.species);
  const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
  let body = `ich heiße ${f.name} und bin ${spDef.article === 'die' ? 'eine' : 'ein'} ${spDef.name}.`;
  const can = L.byClass[f.friendClass];
  if (can) body = rng.next() < 0.25 ? `${body.replace(/\.$/, '')}, und ${lower(can)}` : `${body} ${can}`;
  if (sp) {
    const reason = pick(rng, sp.reason);
    body = rng.next() < 0.5 && L.reasonBridges.length ? `${body} ${pick(rng, L.reasonBridges)} ${lower(reason)}` : `${body} ${reason}`;
    body = `${body} ${pick(rng, sp.wish)}`;
  }
  body = `${body} ${pick(rng, L.closings)}`;
  return [pick(rng, L.openings), body, 'Liebe Grüße', f.name].join('\n');
}

/** Fabi (Runde 7): Eine von mehreren Fassungen eines Texts */
export function pickText(rng: Rng, t: Wording | undefined): string {
  if (t === undefined) return '';
  return Array.isArray(t) ? (t.length ? pick(rng, t) : '') : t;
}

/** Einleitung eines Ereignisses oder Auftrags, in der gewürfelten Fassung */
export function eventText(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'event' && p.kind !== 'errand') return '';
  const def = getEncounter(p.eventId);
  const v = p.kind === 'event' ? p.variant : undefined;
  const text = v !== undefined ? def.variants![v]!.text : def.text;
  const chosen = Array.isArray(text) ? (text[p.textIndex ?? 0] ?? text[0] ?? '') : text;
  return fill(chosen, eventCtx(run));
}

/** Was in allen Texten einer Begegnung eingesetzt wird: das Tier, das Streitobjekt, das Gemerkte (E25, E35) */
export function eventCtx(run: RunState): FillContext {
  const p = run.pending;
  if (p.kind !== 'event' && p.kind !== 'errand') return {};
  const ctx: FillContext = {};
  if (p.kind === 'event') {
    if (p.subject) {
      ctx.tier = displayName(p.subject);
      ctx.tiername = p.subject.name;
    }
    if (p.prize) ctx.beute = itemLabel(p.prize);
  }
  const def = getEncounter(p.eventId);
  // E43: In der Einleitung darf ein Tier aus dem Landtrupp vorkommen; fest gewählt, damit es beim Neuzeichnen bleibt
  const party = p.party.map((id) => run.crew.find((f) => f.id === id)).filter((f): f is RunFriend => !!f);
  if (party.length) ctx.jemand = party[(p.eventId.length + run.landing) % party.length]!.name;
  // E43: Was der Run sich gemerkt hat, etwa wo die Kraniche überwintern
  if (def.knows) ctx.ort = loreOf(run, def.knows);
  const recalls = def.recalls;
  const ref = recalls ? run.notes?.[recalls] : undefined;
  if (ref) {
    ctx.gabe = itemLabel(ref);
    ctx.preis = getItem(ref).price;
  }
  return ctx;
}

/**
 * E43 (Erste Fassung, Runde 4): Was die Tiere erzählen, gilt den ganzen Run: Beim ersten Mal wird es aus run.json (lore)
 * gewürfelt und gemerkt; wer vorher nicht zugehört hat, rät
 */
export function loreOf(run: RunState, key: string, rng?: Rng): string {
  const notes = (run.notes ??= {});
  const k = `lore:${key}`;
  if (notes[k]) return notes[k]!;
  const values = runRules.lore[key] ?? [];
  if (!values.length) return '';
  notes[k] = rng ? pick(rng, values) : withRng(run, (r) => pick(r, values));
  return notes[k]!;
}

/** E25: Wo das Gemerkte gerade ist: im Gepäck oder bei einem Freund angelegt */
export function findRemembered(run: RunState, key: string): { list: string[]; index: number } | null {
  const ref = run.notes?.[key];
  if (!ref) return null;
  for (const list of [run.inventory, ...run.crew.filter((f) => !f.gone).map((f) => f.items)]) {
    const exact = list.indexOf(ref);
    const index = exact >= 0 ? exact : list.findIndex((r) => baseItemId(r) === baseItemId(ref));
    if (index >= 0) return { list, index };
  }
  return null;
}

/** hidden: braucht eine Spezies, die nicht im Landtrupp ist; dann zeigt das Spiel die Option gar nicht (Spieltest Phase 4) */
export type OptionView = {
  id: string;
  text: string;
  hint?: string;
  available: boolean;
  reason?: string;
  hidden?: boolean;
  time: number;
  perTry?: boolean;
  /** E26 (Runde 7): Chance des nächsten Versuchs, wenn sie steigt */
  chance?: number;
  /** E24: Wer es machen kann und wie gut */
  actors?: { id: string; name: string; chance: number }[];
  /** E43: die Frage dazu, etwa „Wer erzählt?“ */
  ask?: string;
  /** E36: Felder des Glücksrads, für die Darstellung */
  wheel?: { label: string; weight: number }[];
  /** Runde 8 (Fabi): Was man verschenken kann; die Option ist eine, das Item wählt man im zweiten Schritt */
  gifts?: { id: string; ref: string; hint: string }[];
};

/** E24 (Fabi): Wie gut eine Spezies das kann, was die Option verlangt (etwa Klettern) */
export function actorChance(o: EventOption, species: string): number {
  return o.actor?.chanceBySpecies[species] ?? o.actor?.default ?? 1;
}

/** Fabi (Runde 7): Was man abgeben kann: ein Item je Art aus dem Gepäck, bei Heilmitteln nur Heilmittel */
export function giftable(run: RunState, o: EventOption): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ref of run.inventory) {
    const def = getItem(ref);
    if (o.gift!.kind === 'heal' && !(def.use?.heal && def.use.heal > 0)) continue;
    if (seen.has(def.id)) continue;
    seen.add(def.id);
    out.push(ref);
  }
  return out;
}

/** E24: Welche Optionen es gibt, hängt davon ab, wer im Landtrupp ist */
export function eventOptions(run: RunState): OptionView[] {
  if (run.pending.kind !== 'event') return [];
  const p = run.pending;
  const def = getEncounter(p.eventId);
  const v = p.variant;
  const opts = (v !== undefined ? def.variants![v]!.options : def.options) ?? [];
  const party = p.party.map((id) => friendById(run, id));
  const views: OptionView[] = [];
  for (const o of opts) {
    if (o.pending) continue;
    const r = o.requires;
    let reason: string | undefined;
    if (r?.species && !party.some((f) => f.species === r.species)) reason = `nur mit ${getSpecies(r.species).name} im Landtrupp`;
    if (r?.item && !run.inventory.some((ref) => baseItemId(ref) === r.item)) reason = `braucht ${getItem(r.item).name}`;
    if (r?.wood && run.wood < r.wood) reason = `braucht ${r.wood} Holz`;
    if (r?.healItem && !weakestHealItem(run)) reason = 'braucht ein Heilmittel, etwa eine Hagebutte';
    if (r?.remembered && !findRemembered(run, r.remembered)) reason = 'ist nicht mehr da';
    if (o.wheel && run.wood < o.wheel.cost) reason = `braucht ${o.wheel.cost} Holz`;
    const price = rememberedPrice(run, o);
    if (o.effects?.some((e) => e.payRemembered) && price.preis !== undefined && run.wood < price.preis) reason = `braucht ${price.preis} Holz`;
    const ctx = { ...eventCtx(run), ...price };
    // Q39: wie lange die Option dauert; bei Versuchen (E26) jeder einzelne
    const base: OptionView = { id: o.id, text: fill(o.text, ctx), available: !reason, time: o.attempt ? attemptTime(o) : optionTime(o) };
    if (o.attempt) base.perTry = true;
    if (o.actor) {
      base.actors = party.filter((f) => !o.actor!.exclude?.includes(f.species)).map((f) => ({ id: f.id, name: displayName(f), chance: actorChance(o, f.species) }));
      // Runde 7: Wer nur Vögel dabeihat, klettert nicht, die fliegen einfach hoch
      if (!base.actors.length) {
        base.available = false;
        base.hidden = true;
        base.reason = 'niemand im Landtrupp klettert';
      }
    }
    if (o.ask) base.ask = o.ask;
    if (r?.species && !party.some((f) => f.species === r.species)) base.hidden = true;
    if (o.hint) base.hint = fill(o.hint, ctx);
    if (o.wheel) base.wheel = o.wheel.segments.map((s) => ({ label: s.label, weight: s.weight }));
    if (reason) base.reason = reason;
    // Fabi (Runde 7): Beim Abgeben wählt man selbst, was; ohne passendes Item bleibt die Option grau
    if (o.gift) {
      const refs = giftable(run, o);
      if (!refs.length) {
        views.push({ ...base, text: fill(o.text, { ...ctx, gabe: o.gift.kind === 'heal' ? 'ein Heilmittel' : 'ein Item' }), available: false, reason: o.gift.kind === 'heal' ? 'braucht ein Heilmittel, etwa eine Hagebutte' : 'braucht ein Item im Gepäck' });
        continue;
      }
      // Runde 8 (Fabi): eine Option, bei zehn Items nicht zehn Knöpfe
      const gifts = refs.map((ref) => ({ id: `${o.id}:${ref}`, ref, hint: giftHint(o, ref) }));
      const rarer = o.gift.heal ? `Je seltener ${o.gift.kind === 'heal' ? 'das Heilmittel' : 'das Geschenk'}, desto mehr erholt sich der Landtrupp.` : '';
      const hint = [rarer, base.hint].filter(Boolean).join(' ') || undefined;
      views.push({ ...base, text: fill(o.text, { ...ctx, gabe: o.gift.kind === 'heal' ? 'ein Heilmittel' : 'etwas' }), gifts, ...(hint ? { hint } : {}) });
      continue;
    }
    views.push(base);
  }
  return views;
}

/** E25 (Runde 7): der Wert des Gemerkten in Holz, für „Holz geben ({preis})“ */
export function rememberedPrice(run: RunState, o: EventOption): { preis?: number } {
  const key = o.effects?.find((e) => e.payRemembered)?.payRemembered ?? o.requires?.remembered;
  const id = key ? run.notes?.[key] : undefined;
  return id ? { preis: getItem(id).price } : {};
}

export const RARITY_WORD: Record<Rarity, string> = { gewoehnlich: 'gewöhnlich', selten: 'selten', 'sehr-selten': 'sehr selten' };

/** Fabi (Runde 7): Beim Verschenken sieht man, was die Seltenheit ausmacht */
export function giftHint(o: EventOption, ref: string): string {
  const rarity = getItem(ref).rarity ?? 'gewoehnlich';
  const share = o.gift?.heal?.[rarity];
  const word = RARITY_WORD[rarity].charAt(0).toUpperCase() + RARITY_WORD[rarity].slice(1);
  const what = share ? `${word}: Der Landtrupp erholt sich um ${Math.round(share * 100)} %.` : `${word}. ${itemText(ref)}`;
  return what;
}

export function findOption(run: RunState, optionId: string): EventOption {
  if (run.pending.kind !== 'event') throw new Error('Kein Ereignis');
  const def = getEncounter(run.pending.eventId);
  const v = run.pending.variant;
  const id = optionId.split(':')[0];
  const o = ((v !== undefined ? def.variants![v]!.options : def.options) ?? []).find((x) => x.id === id);
  if (!o) throw new Error(`Option ${optionId} gibt es nicht`);
  return o;
}

export function chooseOption(run: RunState, optionId: string, actorId?: string): void {
  const p = run.pending;
  if (p.kind !== 'event' || p.done || p.optionId) throw new Error('Gerade gibt es nichts zu wählen');
  // Runde 8: Ein Geschenk heißt „Option:Item“; die Option selbst trägt die Liste
  const view = eventOptions(run).find((o) => o.id === optionId || o.gifts?.some((g) => g.id === optionId));
  if (!view?.available) throw new Error(view?.reason ?? 'Nicht möglich');
  const o = findOption(run, optionId);
  if (o.actor && (!actorId || !p.party.includes(actorId))) throw new Error('Wer aus dem Landtrupp soll das machen?');
  spend(run, optionTime(o));
  // E36 Elster-Glücksrad (Fabi, Runde 7): Drehen kostet Holz, das Rad bleibt auf einem Feld stehen; man kann nochmal
  if (o.wheel) {
    const w = o.wheel;
    run.wood -= w.cost;
    const i = withRng(run, (rng) => {
      let r = rng.next() * w.segments.reduce((a, s) => a + s.weight, 0);
      return Math.max(0, w.segments.findIndex((s) => (r -= s.weight) < 0));
    });
    const seg = w.segments[i] ?? w.segments[w.segments.length - 1]!;
    p.wheel = { segment: i, spins: (p.wheel?.spins ?? 0) + 1 };
    const ctx = applyEffects(run, seg.effects, p.party);
    p.log.push(fill(withRng(run, (rng) => pickText(rng, seg.text)), { ...eventCtx(run), ...ctx }));
    return;
  }
  p.optionId = optionId;
  // E43 Eulen-Legende: Jedes Tier erzählt sie anders; wer erzählt, sucht man sich aus
  if (o.tellBy) {
    const party = p.party.map((id) => friendById(run, id));
    const teller = actorId ? friendById(run, actorId) : withRng(run, (rng) => pick(rng, party));
    return finishOption(run, o.effects ?? [], o.tellBy[teller.species] ?? o.result ?? '', teller, o);
  }
  // E43 Kranichfamilie: Stimmt die Antwort mit dem, was die Kraniche im Moor erzählt haben?
  if (o.quiz && o.outcomes) {
    const right = loreOf(run, o.quiz.key) === o.quiz.value;
    const out = o.outcomes[right ? 0 : 1] ?? o.outcomes[0]!;
    return finishOption(run, out.effects, out.text, undefined, o);
  }
  if (o.actor && o.outcomes) {
    // E24 (Fabi, Spieltest Phase 4): Man wählt selbst, wer es macht; wer es gut kann, scheitert seltener
    const actor = friendById(run, actorId!);
    const ok = withRng(run, (rng) => rng.next() < actorChance(o, actor.species));
    const out = o.outcomes[ok ? 0 : 1] ?? o.outcomes[0]!;
    return finishOption(run, out.effects, out.text, actor, o);
  }
  if (o.attempt) {
    // Runde 7: tries 0 heißt unbegrenzt; eine große Zahl statt Infinity, damit der Spielstand speicherbar bleibt
    p.attemptsLeft = o.attempt.tries > 0 ? o.attempt.tries : 999;
    p.attemptChance = o.attempt.chance;
    return tryAttempt(run);
  }
  if (o.outcomes) {
    const out = withRng(run, (rng) => rollOutcome(rng, o.outcomes!));
    return finishOption(run, out.effects, out.text, undefined, o);
  }
  // Fabi (Runde 7): Abgeben, was man selbst gewählt hat
  if (o.gift) {
    // Ohne gewähltes Item (Harness) das erste, das passt
    const ref = optionId.includes(':') ? optionId.slice(o.id.length + 1) : (giftable(run, o)[0] ?? '');
    const i = run.inventory.indexOf(ref);
    if (i < 0) throw new Error('Nicht im Gepäck');
    run.inventory.splice(i, 1);
    const rarity = getItem(ref).rarity ?? 'gewoehnlich';
    const extra: EventEffect[] = [];
    const share = o.gift.heal?.[rarity];
    if (share) extra.push({ heal: share });
    if (o.gift.flagByRarity) extra.push({ flag: `${o.gift.flagByRarity}-${rarity}` });
    if (o.gift.remember) (run.notes ??= {})[o.gift.remember] = ref;
    return finishOption(run, [...(o.effects ?? []), ...extra], o.gift.resultByRarity?.[rarity] ?? o.result ?? '', undefined, o, { gabe: itemLabel(ref) });
  }
  finishOption(run, o.effects ?? [], o.result ?? '', undefined, o);
}

/** E26 Emil: jeder Versuch kann schiefgehen; nach dem letzten Fehlversuch muss man fliehen. Runde 7: Beim Fuchs steigt die Chance mit jedem Fehlversuch */
export function tryAttempt(run: RunState): void {
  const p = run.pending;
  if (p.kind !== 'event' || !p.optionId || !p.attemptsLeft) throw new Error('Kein Versuch offen');
  const o = findOption(run, p.optionId);
  const a = o.attempt!;
  const chance = p.attemptChance ?? a.chance;
  const ok = withRng(run, (rng) => rng.next() < chance);
  spend(run, attemptTime(o));
  p.attemptsLeft -= 1;
  if (ok) return finishOption(run, a.success.effects, a.success.text, undefined, o);
  if (a.chanceStep) p.attemptChance = Math.min(1, chance + a.chanceStep);
  const ctx = applyEffects(run, a.fail.effects, p.party);
  // Fabi (Runde 7): Der Fuchs sagt jedes Mal etwas anderes, darum die Fassungen der Reihe nach
  p.fails = (p.fails ?? 0) + 1;
  const failText = Array.isArray(a.fail.text) ? (a.fail.text[(p.fails - 1) % a.fail.text.length] ?? '') : a.fail.text;
  p.log.push(fill(failText, { ...eventCtx(run), ...ctx }));
  if (p.attemptsLeft <= 0) finishOption(run, a.giveUp.effects, a.giveUp.text, undefined, o);
}

/** E26: nach einem Fehlversuch aufgeben */
export function giveUpAttempt(run: RunState): void {
  const p = run.pending;
  if (p.kind !== 'event' || !p.optionId || p.done) throw new Error('Kein Versuch offen');
  const o = findOption(run, p.optionId);
  p.attemptsLeft = 0;
  finishOption(run, o.attempt!.giveUp.effects, o.attempt!.giveUp.text, undefined, o);
}

/** E36: Glücksrad verlassen */
export function leaveEvent(run: RunState): void {
  const p = run.pending;
  if (p.kind !== 'event' || p.done) throw new Error('Kein Ereignis offen');
  finishOption(run, [], '');
}

export function rollOutcome<T extends { chance: number }>(rng: Rng, list: T[]): T {
  const total = list.reduce((s, o) => s + o.chance, 0);
  let r = rng.next() * total;
  for (const o of list) {
    r -= o.chance;
    if (r < 0) return o;
  }
  return list[list.length - 1]!;
}

export function finishOption(run: RunState, effects: EventEffect[], text: Wording, actor?: RunFriend, o?: EventOption, extra: FillContext = {}): void {
  const p = run.pending;
  if (p.kind !== 'event') return;
  const def = getEncounter(p.eventId);
  const before = eventCtx(run);
  const ctx = { ...extra, ...applyEffects(run, effects, p.party, actor, p.subject) };
  const party = p.party.map((id) => run.crew.find((f) => f.id === id)).filter((f): f is RunFriend => !!f);
  const jemand = party.length ? withRng(run, (rng) => pick(rng, party).name) : '';
  const full: FillContext = { ...before, ...ctx, jemand, ...(actor ? { name: actor.name } : {}) };
  const chosen = withRng(run, (rng) => pickText(rng, text));
  if (chosen) p.log.push(fill(chosen, full));
  // E32 (Fabi, Runde 8): Zum Schluss kommt eins dazu, etwa der weise Rat des Rothirschs
  if (o?.bonus?.length) {
    const b = withRng(run, (rng) => rollOutcome(rng, o.bonus!.map((x) => ({ ...x, chance: x.weight }))));
    const bctx = applyEffects(run, b.effects, p.party);
    const line = withRng(run, (rng) => pickText(rng, b.text));
    if (line) p.log.push(fill(line, { ...full, ...bctx }));
  }
  // Fabi (Runde 7): Manche Tiere reden mit, etwa die Elster, wenn ihr Glänzendes liegen lasst
  for (const extraLine of o?.resultIf ?? []) {
    if (party.some((f) => f.species === extraLine.species)) p.log.push(fill(withRng(run, (rng) => pickText(rng, extraLine.text)), full));
  }
  p.done = true;
  chronicle(run, def.name, p.log.join(' ') || 'Nichts passiert.');
  const fight = effects.find((e) => e.fight)?.fight;
  if (fight) {
    // E27: Hilferuf und Hinterhalt werden zum Kampf; Runde 7: andere Kämpfe am Ufer (Hagelchen-Nest) sind ganz normale
    startFight(run, p.party, fight.ambush ? 'hinterhalt' : fight.subject ? 'hilferuf' : 'kampf', fight.subject ? p.subject : undefined, fight);
    return;
  }
  if (effects.some((e) => e.recruit) && p.subject) {
    // F39: Wie der Neue zu euch kam; F43: Den Brief behält er, für den Abschied
    if (p.letter) p.subject.letter = p.letter;
    if (p.eventId === runRules.earlyRecruitEvent) remember(run, p.subject, 'bewerbung');
    else remember(run, p.subject, 'ereignis', { wo: def.name });
    run.pending = { kind: 'recruit', candidate: uniqueName(run, p.subject), log: [...p.log], then: null, party: p.party };
  }
}

export type FillContext = { tier?: string; tiername?: string; opfer?: string; beute?: string; gabe?: string; name?: string; holz?: number; jemand?: string; preis?: number; gegner?: string; xp?: number; ort?: string };

/**
 * Platzhalter in Spieltexten: {tier} (mit Spezies), {tiername} (nur der Name), {opfer}, {beute}, {gabe} (was ihr abgebt),
 * {name}, {holz}; Runde 7: {jemand} (ein Tier aus dem Landtrupp), {preis} (Wert in Holz), {gegner} (was die Späher sahen);
 * E43: {ort} (was die Tiere erzählt haben, etwa wo man überwintert)
 */
export function fill(text: string, ctx: FillContext): string {
  return text
    .replaceAll('{holz}', ctx.holz !== undefined ? String(ctx.holz) : 'etwas')
    .replaceAll('{gabe}', ctx.gabe ?? 'ein Heilmittel')
    .replaceAll('{jemand}', ctx.jemand ?? 'jemand')
    .replaceAll('{preis}', ctx.preis !== undefined ? String(ctx.preis) : 'etwas')
    .replaceAll('{gegner}', ctx.gegner ?? 'allerlei Sturmwesen')
    .replaceAll('{xp}', ctx.xp !== undefined ? String(ctx.xp) : 'ein paar')
    .replaceAll('{tiername}', ctx.tiername ?? ctx.tier ?? '')
    .replaceAll('{tier}', ctx.tier ?? '')
    .replaceAll('{opfer}', ctx.opfer ?? 'jemand')
    .replaceAll('{beute}', ctx.beute ?? 'etwas')
    .replaceAll('{name}', ctx.name ?? '')
    .replaceAll('{ort}', ctx.ort ?? 'irgendwo am Meer');
}

/** I22: ein Zufallsfund nach Seltenheit; 'equip' und 'consumable' für eine Art, rarity für eine Seltenheit */
export function rollItem(rng: Rng, kind: 'random' | 'equip' | 'consumable', rarity?: Rarity, star = 1): string {
  let pool = allItems.filter((i) => itemOffered(i) && (kind === 'random' || i.kind === kind) && (!rarity || (i.rarity ?? 'gewoehnlich') === rarity));
  if (!pool.length) pool = allItems.filter((i) => itemOffered(i));
  return newItem(rng, pickByRarity(rng, pool).id, star);
}

/** Die Bausteine eines Ereignisses (E27) */
export function applyEffects(run: RunState, effects: EventEffect[], party: string[], actor?: RunFriend, subject?: RunFriend): FillContext {
  const ctx: FillContext = {};
  withRng(run, (rng) => {
    const members = party.map((id) => run.crew.find((f) => f.id === id)).filter((f): f is RunFriend => !!f);
    for (const e of effects) {
      if (e.injure) {
        // Rückschläge ja, Trauma nein (P3): Ereignisse verletzen, schicken aber niemanden in den Abschied
        const who = e.injure === 'all' ? members : e.injure === 'actor' ? (actor ? [actor] : []) : members.length ? [pick(rng, members)] : [];
        for (const f of who) {
          // R28 (Fabi, Runde 7): je Tier gewürfelt; wer schon unter einem Drittel Leben hat, verliert immer weniger
          const max = maxHp(f, run);
          let loss = max * rollShare(rng, e.share ?? 0.2);
          if (f.hp < max / 3) loss *= f.hp / (max / 3);
          f.hp = Math.max(1, f.hp - loss);
        }
        ctx.opfer = who.map((f) => f.name).join(' und ');
      }
      if (e.injureCurrent && members.length) {
        // E30 Hilferuf-Falle (Fabi, Runde 7): Flucht kostet Leben vom jetzigen Stand, nie den Abschied
        const { mode, share } = e.injureCurrent;
        if (mode === 'one') {
          const f = pick(rng, members);
          f.hp = Math.max(1, f.hp * (1 - share));
          ctx.opfer = f.name;
        } else {
          const pool = (share * members.reduce((a, f) => a + f.hp, 0)) / members.length;
          const weights = members.map(() => 0.2 + rng.next());
          const sum = weights.reduce((a, w) => a + w, 0);
          members.forEach((f, i) => (f.hp = Math.max(1, f.hp - (pool * weights[i]!) / sum)));
          ctx.opfer = members.map((f) => f.name).join(' und ');
        }
      }
      if (e.wood) {
        const w = rollRange(rng, e.wood);
        run.wood = Math.max(0, run.wood + w);
        if (w > 0) ctx.holz = w;
      }
      if (e.item) {
        // I22: Zufallsfunde nach Seltenheit gewichtet
        const kind = e.item === 'equip' || e.item === 'consumable' ? e.item : null;
        const random = kind !== null || e.item === 'random';
        const prize = e.item === 'prize' && run.pending.kind === 'event' ? run.pending.prize : undefined;
        const id = prize ?? (random ? rollItem(rng, kind ?? 'random', e.rarity, starFactor(run)) : newItem(rng, e.item, starFactor(run)));
        run.inventory.push(id);
        ctx.beute = itemLabel(id);
        if (e.remember) (run.notes ??= {})[e.remember] = id;
      }
      if (e.artifact) {
        // I13 (Fabi, Runde 7): ein Artefakt, das man noch nicht hat
        const left = allArtifacts.filter((a) => !run.artifacts.includes(a.id) && artifactOffered(a));
        if (left.length) {
          const a = pickByRarity(rng, left);
          run.artifacts.push(a.id);
          ctx.beute = a.name;
        } else run.wood += 10;
      }
      if (e.consume) {
        const i = run.inventory.findIndex((ref) => baseItemId(ref) === e.consume);
        if (i >= 0) run.inventory.splice(i, 1);
      }
      if (e.returnRemembered) {
        // E35: Zurückgeben, auch wenn es schon jemand angelegt hat
        const at = findRemembered(run, e.returnRemembered);
        if (at) {
          ctx.gabe = itemLabel(at.list[at.index]!);
          at.list.splice(at.index, 1);
          for (const f of run.crew) f.hp = Math.min(f.hp, maxHp(f, run));
        }
      }
      if (e.payRemembered) {
        const id = run.notes?.[e.payRemembered];
        if (id) {
          ctx.preis = getItem(id).price;
          run.wood = Math.max(0, run.wood - ctx.preis);
        }
      }
      if (e.consumeHeal) {
        // Fabi, Spieltest Phase 4: irgendein Heilmittel abgeben, und zwar das schwächste
        const id = weakestHealItem(run);
        if (id) {
          run.inventory.splice(run.inventory.indexOf(id), 1);
          ctx.gabe = itemLabel(id);
        }
      }
      // E29 (Fabi): Heilen am Ufer, für den ganzen Landtrupp
      if (e.heal) for (const f of members) heal(run, f, rollShare(rng, e.heal));
      // Fabi (Runde 7): Bei einem Fest erholen sich alle an Bord, jeder ein bisschen anders
      if (e.healCrew) for (const f of run.crew) if (!f.away) heal(run, f, rollShare(rng, e.healCrew));
      if (e.reveal) for (let k = 1; k <= e.reveal; k++) if (run.landing + k < run.landings.length) run.revealed.push(run.landing + k);
      if (e.flag) run.flags[e.flag] = true;
      if (e.unflag) delete run.flags[e.unflag];
      if (e.later) schedule(run, rng, e.later, e.within ?? [2, 5]);
      if (e.crewSlot) run.crewSlots += e.crewSlot;
      if (e.levelUp && actor) gainXp(run, actor, xpForLevel(actor.level + e.levelUp) - actor.xp);
      if (e.farewell && actor) leave(run, actor, 'weggeblieben', `kommt von einem Auftrag nicht wieder (Tag ${run.day})`);
      // E32 (Fabi, Runde 8): Geschichten machen klüger, eine Abkürzung bringt Vorsprung
      if (e.xpParty) {
        const xp = rollRange(rng, e.xpParty);
        for (const f of members) gainXp(run, f, xp);
        ctx.xp = xp;
      }
      if (e.lead) run.lead += e.lead;
      // E43: Die Tiere erzählen etwas, das der Run sich merkt
      if (e.tell) ctx.ort = loreOf(run, e.tell, rng);
      // R38: Mit dem Unwetter als Uhr weiß man jeden Morgen, wer am Durchlass wartet; dann bleibt es beim Wissen von morgens
      if (e.scoutBoss && !stormClockOn()) {
        // E31 Eichelhäher (Fabi, Runde 7): Die Späher sehen, welche Sturmwesen heute am Durchlass warten; so bleibt es
        run.bossPreview = { day: run.day, enemies: generateFoes(rng, bossRequest(run)) };
        ctx.gegner = foeSummary(run.bossPreview.enemies);
      }
      if (e.recruit && !subject && actor) {
        // E4: Wer vom Auftrag jemanden mitbringt
        const [lo, hi] = runRules.enemyBudget.eventLevel[run.day - 1] ?? [1, 1];
        const c = makeFriend(run, rng, { level: lo! + Math.floor(rng.next() * (hi! - lo! + 1)), how: 'auftrag' });
        remember(run, c, 'auftrag', { wer: actor.name });
        run.recruitQueue.push(c);
        ctx.opfer = displayName(c);
        ctx.tiername = c.name;
      }
    }
  });
  return ctx;
}

/** E31: „ein Großer Treibholzer, zwei Schlammlinge und ein Hagelchen“ */
export function foeSummary(enemies: EnemyPlacement[]): string {
  const count = new Map<string, number>();
  for (const e of enemies) if (e.enemy) count.set(e.enemy, (count.get(e.enemy) ?? 0) + 1);
  const words = ['ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht'];
  const parts = [...count].map(([id, n]) => {
    const e = getEnemy(id);
    if (n === 1) return `${e.article === 'die' ? 'eine' : 'ein'} ${e.name}`;
    return `${words[n - 1] ?? n} ${e.plural ?? e.name}`;
  });
  return parts.length <= 1 ? (parts[0] ?? 'niemand') : `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

/** E25: Eine Folge-Begegnung wartet später am selben Tag */
export function schedule(run: RunState, rng: Rng, eventId: string, within: number[]): void {
  const [a, b] = [within[0] ?? 2, within[1] ?? 5];
  const at = run.landing + a + Math.floor(rng.next() * (b - a + 1));
  if (at >= run.landings.length) return;
  const l = run.landings[at]!;
  run.landings[at] = { category: 'ereignis', eventId, sign: rollSign(rng, 'ereignis', runRules, eventId), seed: l.seed, scheduled: true };
}

/** Ein Freund aus dem Landtrupp geht los; was passiert, steht fest, man erfährt es bei seiner Rückkehr */
export function sendOnErrand(run: RunState, friendId: string | null): void {
  const p = run.pending;
  if (p.kind !== 'errand' || p.done) throw new Error('Gerade gibt es keinen Auftrag');
  const def = getEncounter(p.eventId);
  if (friendId === null) {
    // E4 (Fabi, Doku 16.10.2026): Ablehnen kann eine eigene kleine Szene haben
    if (def.decline) {
      const ctx = applyEffects(run, def.decline.effects ?? [], p.party);
      p.log.push(fill(withRng(run, (rng) => pickText(rng, def.decline!.text)), { ...eventCtx(run), ...ctx }));
    } else p.log.push('Ihr lehnt ab.');
  } else {
    if (!p.party.includes(friendId)) throw new Error('Nur wer im Landtrupp ist, kann gehen');
    // Runde 7 (Spiel-Bot): Wer als Letzter an Bord ist, kann nicht gehen, sonst ist niemand mehr da, der an Land geht
    if (!run.crew.some((c) => c.id !== friendId && !c.away)) throw new Error('Wer als Letzter an Bord ist, kann nicht gehen');
    const f = friendById(run, friendId);
    const outcome = withRng(run, (rng) => {
      const r = rng.next() * def.outcomes!.reduce((s, o) => s + o.chance, 0);
      let acc = 0;
      return def.outcomes!.findIndex((o) => (acc += o.chance) > r);
    });
    f.away = { haltsLeft: runRules.awayHalts, errand: def.id, outcome: Math.max(0, outcome), since: run.landing };
    spend(run, runRules.storm.time.errand);
    p.log.push(`${f.name} macht sich auf den Weg.`);
  }
  p.done = true;
  chronicle(run, def.name, p.log.join(' '));
}

/** E4: Wer unterwegs war, kommt nach seinen Halten zurück und erzählt; am Durchlass kommen alle zurück */
export function returnErrands(run: RunState, all: boolean): void {
  for (const f of [...run.crew]) {
    if (!f.away || (!all && (f.away.haltsLeft ?? 0) > 0)) continue;
    const def = getEncounter(f.away.errand);
    const out = def.outcomes![f.away.outcome]!;
    f.away = null;
    const level = f.level;
    const wood = run.wood;
    const ctx = applyEffects(run, out.effects, [f.id], f);
    // E4 (Fabi, Spieltest Phase 4): Wer sich unterwegs verletzt, hat wenigstens etwas gelernt [ZAHL?]
    if (out.effects.some((e) => e.injure) && !out.effects.some((e) => e.farewell)) gainXp(run, f, runRules.xp.errandConsolation);
    chronicle(run, 'Zurück vom Auftrag', fill(withRng(run, (rng) => pickText(rng, out.text)), { ...ctx, name: f.name, beute: ctx.beute, tier: ctx.opfer }));
    if (!f.gone) remember(run, f, 'errand', { auftrag: def.name });
    (run.reports ??= []).push(errandReport(run, f, def.name, out.effects, ctx, { level, wood }));
  }
  if (run.recruitQueue.length && run.status === 'running') {
    const candidate = run.recruitQueue.shift()!;
    run.pending = { kind: 'recruit', candidate: uniqueName(run, candidate), log: [], then: { kind: 'river' }, party: [] };
  }
}

/**
 * E4 (Fabi, Spieltest Phase 4): Wer zurückkommt, erzählt, wie es war, in der Stimme seiner Spezies; darunter steht
 * nüchtern, was er mitbringt. Wer fernbleibt, hinterlässt einen Zettel. Die Sätze hängen nicht am Zufall des Runs.
 */
export function errandReport(run: RunState, f: RunFriend, errand: string, effects: EventEffect[], ctx: FillContext, before: { level: number; wood: number }): ErrandReport {
  const t = reportTexts;
  const k = run.step + f.name.length;
  const one = (list: string[]) => list[k % list.length]!;
  const fillIn = (s: string) =>
    fill(s, { beute: ctx.beute, tier: ctx.opfer, tiername: ctx.tiername ?? ctx.opfer ?? '' }).replaceAll('{holz}', String(run.wood - before.wood));
  const gone = effects.some((e) => e.farewell);
  if (gone) return { friendId: f.id, name: f.name, species: f.species, errand, letter: `${one(t.farewell.lines)}\n${f.name}`, facts: [`${f.name} kommt nicht wieder.`], gone };
  const voice = t.voices[f.species] ?? { hello: ['Bin wieder da.'], bye: [''] };
  const lines = [one(voice.hello)];
  const facts: string[] = [];
  if (f.level > before.level) {
    lines.push(one(t.lines.levelUp));
    facts.push(`Level ${f.level} erreicht`);
  }
  if (ctx.beute) {
    lines.push(fillIn(one(t.lines.item)));
    facts.push(`${ctx.beute} liegt im Inventar`);
  }
  if (run.wood > before.wood) {
    // Fabi (Phase Kern): Nach einem Item klingt der Satz zum Holz nach einer Zugabe, sonst liest er sich, als ginge es ums Item
    lines.push(one(ctx.beute ? t.lines.woodToo : t.lines.wood));
    facts.push(`+${run.wood - before.wood} Holz`);
  }
  if (effects.some((e) => e.recruit) && ctx.opfer) {
    lines.push(fillIn(one(t.lines.recruit)));
    facts.push(`${ctx.opfer} will mitreisen`);
  }
  // E31 (Fabi, Runde 7): Was die Späher am Durchlass gesehen haben
  if (ctx.gegner) {
    lines.push(`Ich habe gesehen, wer heute am Durchlass wartet: ${ctx.gegner}.`);
    facts.push(`Am Durchlass warten heute: ${ctx.gegner}`);
  }
  if (effects.some((e) => e.injure)) {
    lines.push(one(t.lines.injure));
    facts.push(`verletzt: noch ${Math.round((f.hp / maxHp(f, run)) * 100)} % Leben`);
    facts.push(`+${runRules.xp.errandConsolation} XP, unterwegs etwas gelernt`);
  }
  if (lines.length === 1) lines.push(one(t.lines.nothing));
  // Fabi: Wer zurückkommt, erzählt; ein Zettel mit Unterschrift nur, wenn jemand fernbleibt
  lines.push(one(voice.bye));
  return { friendId: f.id, name: f.name, species: f.species, errand, letter: lines.join('\n'), facts, gone };
}

/** Bericht gelesen */
export function dismissReport(run: RunState): void {
  run.reports?.shift();
}
